'use client';

import { Loader2Icon, SendHorizontalIcon, SquareIcon, XIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type ComponentProps,
  type FormEvent,
  type FormEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CHAT_STATUS, type ChatStatus } from '@/utils/constants';
import { cn } from '@/utils/utils';

const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

export interface FileUIPart {
  filename?: string;
  mediaType?: string;
  type: 'file';
  url?: string;
}

interface AttachmentsContext {
  add: (files: File[] | FileList) => void;
  fileInputRef: RefObject<HTMLInputElement | null>;
  files: Array<FileUIPart & { id: string }>;
  openFileDialog: () => void;
}

const AttachmentsContext = createContext<AttachmentsContext | null>(null);

export const usePromptInputAttachments = () => {
  const context = useContext(AttachmentsContext);

  if (!context) {
    throw new Error('usePromptInputAttachments must be used within a PromptInput');
  }

  return context;
};

export interface PromptInputMessage {
  files?: Array<FileUIPart>;
  text?: string;
}

export type { ChatStatus };

export type PromptInputProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit' | 'onError'> & {
  accept?: Array<string>;
  maxFiles?: number;
  maxFileSize?: number;
  multiple?: boolean;
  onError?: (err: { code: 'max_files' | 'max_file_size' | 'accept'; message: string }) => void;
  onSubmit: (message: PromptInputMessage, event: FormEvent<HTMLFormElement>) => void;
};

export const PromptInput = ({
  accept,
  className,
  maxFiles,
  maxFileSize,
  multiple,
  onError,
  onSubmit,
  ...props
}: PromptInputProps) => {
  const [items, setItems] = useState<Array<FileUIPart & { id: string }>>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itemsRef = useRef<Array<FileUIPart & { id: string }>>([]);

  const openFileDialog = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const matchesAccept = useCallback(
    (f: File) => {
      if (!accept || accept.length === 0) {
        return true;
      }

      const fileName = f.name.toLowerCase();
      const fileExtension = fileName.slice(fileName.lastIndexOf('.'));
      return accept.some((ext) => {
        const normalizedExt = ext.toLowerCase().startsWith('.')
          ? ext.toLowerCase()
          : `.${ext.toLowerCase()}`;
        return fileExtension === normalizedExt;
      });
    },
    [accept],
  );

  const add = useCallback(
    (files: File[] | FileList) => {
      const incoming = Array.from(files);
      const accepted = incoming.filter((f) => matchesAccept(f));
      if (accepted.length === 0) {
        const supportedExtensions = accept?.join(', ') ?? 'any file type';
        onError?.({
          code: 'accept',
          message: `No files match the accepted types. Supported file extensions: ${supportedExtensions}`,
        });
        return;
      }
      const withinSize = (f: File) => (maxFileSize ? f.size <= maxFileSize : true);
      const sized = accepted.filter(withinSize);
      if (sized.length === 0 && accepted.length > 0) {
        onError?.({
          code: 'max_file_size',
          message: 'All files exceed the maximum size.',
        });
        return;
      }
      setItems((prev) => {
        const capacity =
          typeof maxFiles === 'number' ? Math.max(0, maxFiles - prev.length) : undefined;
        const capped = typeof capacity === 'number' ? sized.slice(0, capacity) : sized;
        if (typeof capacity === 'number' && sized.length > capacity) {
          onError?.({
            code: 'max_files',
            message: 'Too many files. Some were not added.',
          });
        }
        const next: Array<FileUIPart & { id: string }> = [];
        for (const file of capped) {
          next.push({
            filename: file.name,
            id: generateId(),
            mediaType: file.type,
            type: 'file',
            url: URL.createObjectURL(file),
          });
        }
        const newItems = prev.concat(next);
        itemsRef.current = newItems;
        return newItems;
      });
    },
    [matchesAccept, maxFiles, maxFileSize, onError, accept],
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    if (event.currentTarget.files) {
      // Add selected files
      add(event.currentTarget.files);
      // Allow re-selecting the same file again later by resetting the input value
      event.currentTarget.value = '';
    }
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const files: Array<FileUIPart> = items.map(({ ...item }) => ({
      ...item,
    }));

    const formData = new FormData(event.currentTarget);
    const text = (formData.get('message') as string) || '';

    // Clean up blob URLs after submission
    items.forEach((item) => {
      if (item.url) {
        URL.revokeObjectURL(item.url);
      }
    });

    onSubmit({ files, text }, event);
    setItems([]);
    itemsRef.current = [];
  };

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => {
        if (item.url) {
          URL.revokeObjectURL(item.url);
        }
      });
    };
  }, []);

  const ctx = useMemo<AttachmentsContext>(
    () => ({
      add,
      fileInputRef: inputRef,
      files: items.map((item) => ({ ...item, id: item.id })),
      openFileDialog,
    }),
    [items, add, openFileDialog],
  );

  return (
    <AttachmentsContext.Provider value={ctx}>
      <input
        accept={accept ? accept.join(',') : undefined}
        className="hidden"
        multiple={multiple}
        onChange={handleChange}
        placeholder="Attach files"
        ref={inputRef}
        title="Attach files"
        type="file"
      />
      <form
        className={cn(
          'bg-background w-full overflow-hidden rounded-xl border shadow-sm',
          className,
        )}
        onSubmit={handleSubmit}
        {...props}
      />
    </AttachmentsContext.Provider>
  );
};

export type PromptInputBodyProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputBody = ({ className, ...props }: PromptInputBodyProps) => (
  <div className={cn(className, 'flex flex-col')} {...props} />
);

export type PromptInputTextareaProps = ComponentProps<typeof Textarea>;

const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
  if (e.key === 'Enter') {
    // Don't submit if IME composition is in progress
    if (e.nativeEvent.isComposing) {
      return;
    }

    if (e.shiftKey) {
      // Allow newline
      return;
    }

    // Submit on Enter (without Shift)
    e.preventDefault();
    const form = e.currentTarget.form;
    if (form) {
      form.requestSubmit();
    }
  }
};

export const PromptInputTextarea = ({
  className,
  onChange,
  onKeyDown: onKeyDownProp,
  placeholder = 'What would you like to know?',
  ref,
  ...props
}: PromptInputTextareaProps) => {
  const combinedKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    onKeyDownProp?.(e);
    if (!e.defaultPrevented) {
      handleKeyDown(e);
    }
  };

  return (
    <Textarea
      className={cn(
        'w-full resize-none rounded-none border-none p-3 shadow-none ring-0 outline-none',
        'field-sizing-content bg-transparent dark:bg-transparent',
        'max-h-48 min-h-16',
        'focus-visible:ring-0',
        className,
      )}
      name="message"
      onChange={(e) => {
        onChange?.(e);
      }}
      onKeyDown={combinedKeyDown}
      placeholder={placeholder}
      ref={ref}
      {...props}
    />
  );
};

export type PromptInputToolbarProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputToolbar = ({ className, ...props }: PromptInputToolbarProps) => (
  <div className={cn('flex items-center justify-between p-1', className)} {...props} />
);

export type PromptInputToolsProps = HTMLAttributes<HTMLDivElement>;

export const PromptInputTools = ({ className, ...props }: PromptInputToolsProps) => (
  <div
    className={cn('flex items-center gap-1', '[&_button:first-child]:rounded-bl-xl', className)}
    {...props}
  />
);

export type PromptInputSubmitProps = ComponentProps<typeof Button> & {
  status?: ChatStatus;
};

export const PromptInputSubmit = ({
  children,
  className,
  size = 'icon',
  status,
  variant = 'default',
  ...props
}: PromptInputSubmitProps) => {
  let Icon = <SendHorizontalIcon className="mb-0.5 ml-0.5 size-3" />;

  if (status === CHAT_STATUS.SUBMITTED) {
    Icon = <Loader2Icon className="size-3 animate-spin" />;
  } else if (status === CHAT_STATUS.STREAMING) {
    Icon = <SquareIcon className="bg-primary-foreground size-2" />;
  } else if (status === CHAT_STATUS.ERROR) {
    Icon = <XIcon className="size-3" />;
  }

  return (
    <Button
      className={cn('gap-1.5 rounded-lg', className)}
      size={size}
      type="submit"
      variant={variant}
      {...props}
    >
      {children ?? Icon}
    </Button>
  );
};
